/*
 * Abdi Efendi — sepete ekle akışı
 * - Sayfadaki "Sepete ekle" formlarını (product-form-component dışındakiler) AJAX ile gönderir,
 *   temanın yandan açılan sepet çekmecesini açar ve günceller.
 * - Hediyeleri otomatik ekler/kaldırır: sepet ₺500'yi geçince Tarif Defteri, her 750 ml şişeye
 *   bir Akıtıcı. Hediyelerin ₺0 olması Shopify'daki otomatik indirimlerle olur.
 */
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const config = window.AbdiCart || {};
// Hediyeler: { variantId, threshold } (sepet tutarı eşiği) ya da { variantId, perProductId } (ürün başına 1 adet)
const GIFTS = (config.gifts || []).filter((g) => Number(g.variantId) > 0);
const GIFT_PROP = '_hediye';

const json = (url, body) =>
  fetch(url, {
    method: body ? 'POST' : 'GET',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: body ? JSON.stringify(body) : undefined,
  }).then((r) => r.json());

const sectionIds = () =>
  [...document.querySelectorAll('cart-items-component[data-section-id]')].map((el) => el.dataset.sectionId);

const isGiftLine = (item) => Boolean(item.properties && item.properties[GIFT_PROP]);

/** Her hediye için sepette olması gereken adet */
function wantedQty(gift, cart) {
  const giftVariants = GIFTS.filter((g) => !g.perProductId).map((g) => Number(g.variantId));
  const paidLines = cart.items.filter((i) => !isGiftLine(i) && !giftVariants.includes(i.variant_id));
  if (gift.perProductId) {
    return paidLines.filter((i) => i.product_id === Number(gift.perProductId)).reduce((n, i) => n + i.quantity, 0);
  }
  const paid = paidLines.reduce((sum, i) => sum + i.original_line_price, 0);
  return paid > 0 && paid >= Number(gift.threshold || 0) ? 1 : 0;
}

/** Hediye kurallarını uygular; son isteğin bölüm HTML'lerini döndürür. */
async function syncGift(sections) {
  let cart = await json('/cart.js');
  let lastSections = null;
  // Artık hediye olmayan ürünlerin eski hediye satırları (ör. ₺250'lik eski defter) sepetten çıkarılır
  const giftIds = GIFTS.map((g) => Number(g.variantId));
  for (const stale of cart.items.filter((i) => isGiftLine(i) && !giftIds.includes(i.variant_id))) {
    const res = await json('/cart/change.js', { id: stale.key, quantity: 0, sections: sections.join(',') });
    if (res && res.sections) lastSections = res.sections;
  }
  if (lastSections) cart = await json('/cart.js');
  for (const gift of GIFTS) {
    const want = wantedQty(gift, cart);
    // Sepet tutarına bağlı hediye ayrı bir ₺0'lik üründür: elle eklenmiş olsa bile kurala göre eklenir/çıkarılır.
    // Akıtıcı ise ayrıca satılan bir üründür; yalnızca hediye olarak eklenen satırlara dokunulur.
    const lines = cart.items.filter(
      (i) => i.variant_id === Number(gift.variantId) && (isGiftLine(i) || !gift.perProductId)
    );
    const have = lines.reduce((n, i) => n + i.quantity, 0);
    if (want === have) continue; // Shopify indirimli kısmı ayrı satıra bölebilir; adet doğruysa dokunma
    let res;
    if (lines.length === 0) {
      res = await json('/cart/add.js', {
        items: [{ id: Number(gift.variantId), quantity: want, properties: { [GIFT_PROP]: '1' } }],
        sections: sections.join(','),
      });
    } else {
      // fazla satırları sıfırla, ilk satırı istenen adede getir
      for (const extra of lines.slice(1)) await json('/cart/change.js', { id: extra.key, quantity: 0 });
      res = await json('/cart/change.js', { id: lines[0].key, quantity: want, sections: sections.join(',') });
    }
    if (res && res.sections) lastSections = res.sections;
    cart = await json('/cart.js');
  }
  return { cart, sections: lastSections };
}

// Eşitlemeler sırayla çalışır: aynı anda iki eşitleme olursa ikisi de hediyeyi ekleyip adedi ikiye çıkarıyordu.
let chain = Promise.resolve();
function syncGiftQueued(sections) {
  const run = chain.then(() => syncGift(sections));
  chain = run.catch(() => {});
  return run;
}

/** Eşitleme sonucunu sepet bölümlerine yansıtır (kendi olayımız olduğu için dinleyici tekrar tetiklenmez). */
function publish(gift) {
  if (!gift.sections) return;
  const deferred = CartLinesUpdateEvent.createPromise();
  ownDispatch = true;
  document.dispatchEvent(new CartLinesUpdateEvent({ action: 'update', context: 'cart', lines: [], promise: deferred.promise }));
  ownDispatch = false;
  deferred.resolve({
    cart: CartLinesUpdateEvent.createCartFromAjaxResponse(gift.cart),
    detail: { didError: false, items: gift.cart.items, sections: gift.sections, source: 'abdi-cart' },
  });
}

let ownAdd = false;
let ownDispatch = false;

async function addFromForm(form, submitter) {
  const formData = new FormData(form);
  const sections = sectionIds();
  if (sections.length) formData.append('sections', sections.join(','));

  const deferred = CartLinesUpdateEvent.createPromise();
  ownAdd = true;
  form.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: String(formData.get('id')), quantity: Number(formData.get('quantity')) || 1 }],
      promise: deferred.promise,
    })
  );
  ownAdd = false;

  submitter?.setAttribute('aria-busy', 'true');
  try {
    const added = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: formData,
    }).then((r) => r.json());

    if (added.status) {
      const cart = await json('/cart.js');
      deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { didError: true } });
      alert(added.description || added.message || 'Ürün sepete eklenemedi.');
      return;
    }

    const gift = await syncGiftQueued(sections);
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(gift.cart),
      detail: { didError: false, items: gift.cart.items, sections: gift.sections || added.sections, source: 'abdi-cart' },
    });
  } catch (error) {
    deferred.reject(error);
    form.submit(); // her ihtimale karşı klasik gönderim
  } finally {
    submitter?.removeAttribute('aria-busy');
  }
}

document.addEventListener('submit', (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement)) return;
  const action = form.getAttribute('action') || '';
  if (!/\/cart\/add(\.js)?$/.test(action.split('?')[0])) return;
  if (form.closest('product-form-component')) return; // temanın kendi formu kendini yönetir
  event.preventDefault();
  addFromForm(form, event.submitter);
});

/** Sepet çekmecesini sunucudaki güncel hâliyle yeniden çizer. */
async function refreshSections() {
  const ids = sectionIds();
  if (!ids.length) return;
  const [cart, sections] = await Promise.all([
    json('/cart.js'),
    fetch(`${window.location.pathname}?sections=${ids.join(',')}`, { headers: { Accept: 'application/json' } }).then((r) => r.json()),
  ]);
  publish({ cart, sections });
}

// Sepette başka bir değişiklik olunca (adet, silme, temanın kendi sepete ekle butonları) hediye kuralını yeniden uygula.
// Temanın kendi güncellemesi bizimkinden sonra ekrana basılabiliyor (ör. 750 ml 2→1 olunca ₺75'lik ikinci akıtıcı
// ekranda kalıyordu); bu yüzden düzeltmeden kısa süre sonra çekmeceyi sunucudan taze hâliyle bir kez daha çiz.
document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  if (ownAdd || ownDispatch || !GIFTS.length) return;
  if (event.detail && event.detail.source === 'abdi-cart') return;
  Promise.resolve(event.promise)
    .then(() => syncGiftQueued(sectionIds()))
    .then(publish)
    .then(() => new Promise((r) => setTimeout(r, 400)))
    .then(refreshSections)
    .catch(() => {});
});

// Sayfa açılışında sepeti bir kez kontrol et: eski oturumdan kalan yalnız hediyeleri temizler,
// eksik ya da fazla hediyeyi düzeltir ve sepet çekmecesini sessizce günceller.
(async () => {
  if (!GIFTS.length) return;
  try {
    const cart = await json('/cart.js');
    if (!cart.items.length) return;
    publish(await syncGiftQueued(sectionIds()));
  } catch (e) {
    /* sessizce geç */
  }
})();
